import React, { useState } from 'react';
import {
  Layers,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  Play,
  Lock,
  Search,
  Code,
  Sliders,
  FileText
} from 'lucide-react';
import { AnalyticsEventDefinition } from '../../types';
import { analyticsEngine } from '../../services/analyticsEngine';

export const EventRegistryView: React.FC = () => {
  const [events] = useState<AnalyticsEventDefinition[]>(analyticsEngine.getEventRegistry());
  const [selectedEvent, setSelectedEvent] = useState<AnalyticsEventDefinition | null>(events[0] || null);

  // Interactive Test SDK State
  const [testEventName, setTestEventName] = useState('salary_calculation_completed');
  const [testPayloadJson, setTestPayloadJson] = useState('{\n  "country": "US",\n  "jurisdiction": "CA",\n  "exactSalary": 87432,\n  "userEmail": "john@example.com"\n}');
  const [testResult, setTestResult] = useState<any>(null);

  const handleRunTestTrack = () => {
    try {
      const parsedProps = JSON.parse(testPayloadJson);
      const res = analyticsEngine.track(testEventName, parsedProps);
      setTestResult(res);
    } catch (err: any) {
      setTestResult({ error: `JSON Parsing Error: ${err.message}` });
    }
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Hero Banner */}
      <div className="bg-slate-900 text-white rounded-2xl p-8 shadow-xl space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-xs font-bold border border-blue-500/30">
          <Layers className="w-3.5 h-3.5" />
          Typed Analytics Event Registry & Schema Contract
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight">YamooPay Event Registry & Property Governance</h1>
        <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
          Centralized system of record defining event versioning, property types, privacy classifications, and destination allowlists (`object_action` convention).
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Event List */}
        <div className="lg:col-span-6 space-y-4">
          <h2 className="text-lg font-bold text-slate-900 flex items-center justify-between">
            <span>Registered Analytics Events ({events.length})</span>
            <span className="text-xs font-mono font-bold text-blue-600">
              Versioned & Typed
            </span>
          </h2>

          <div className="space-y-3">
            {events.map((evt) => (
              <div
                key={evt.eventName}
                onClick={() => setSelectedEvent(evt)}
                className={`p-4 rounded-xl border transition-all cursor-pointer space-y-2 ${
                  selectedEvent?.eventName === evt.eventName
                    ? 'bg-blue-50/60 border-blue-300 shadow-sm'
                    : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold bg-slate-900 text-white px-2 py-0.5 rounded">
                      {evt.eventName}
                    </span>
                    <span className="text-[10px] font-mono bg-blue-100 text-blue-800 font-bold px-1.5 py-0.5 rounded">
                      v{evt.eventVersion}
                    </span>
                  </div>

                  <span className={`px-2 py-0.5 rounded text-[10px] font-extrabold ${
                    evt.privacyClass === 'PUBLIC_BEHAVIOR'
                      ? 'bg-emerald-100 text-emerald-800'
                      : evt.privacyClass === 'PRODUCT_USAGE'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-purple-100 text-purple-800'
                  }`}>
                    {evt.privacyClass}
                  </span>
                </div>

                <p className="text-xs text-slate-600">{evt.description}</p>

                <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1 border-t border-slate-100 font-mono">
                  <span>Domain: <strong className="text-slate-800">{evt.domain}</strong></span>
                  <span>Owner: <strong className="text-slate-700">{evt.owner}</strong></span>
                  <span>Destinations: {evt.allowedDestinations.join(', ')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column: Schema Inspector & Interactive SDK Tester */}
        <div className="lg:col-span-6 space-y-6">
          {selectedEvent && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5">
              <div className="border-b border-slate-100 pb-3 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600">
                    Schema Contract Inspector
                  </span>
                  <h3 className="text-lg font-bold text-slate-900">{selectedEvent.eventName}</h3>
                </div>
                <span className="px-2.5 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-mono font-bold">
                  Status: {selectedEvent.status}
                </span>
              </div>

              <div className="space-y-2 text-xs text-slate-700">
                <div><strong>Trigger:</strong> {selectedEvent.trigger}</div>
                <div><strong>Allowed Destinations:</strong> {selectedEvent.allowedDestinations.join(', ')}</div>
              </div>

              {/* Property Registry Table */}
              <div className="space-y-2">
                <div className="text-xs font-bold text-slate-800">
                  Allowed Properties ({selectedEvent.properties.length})
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 font-bold uppercase text-[10px]">
                        <th className="py-2 px-2">Property</th>
                        <th className="py-2 px-2">Type</th>
                        <th className="py-2 px-2">Privacy</th>
                        <th className="py-2 px-2">Req</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 font-mono text-[11px] text-slate-700">
                      {selectedEvent.properties.map((p) => (
                        <tr key={p.name}>
                          <td className="py-2 px-2 font-bold text-slate-900">{p.name}</td>
                          <td className="py-2 px-2 text-blue-600">{p.type}</td>
                          <td className="py-2 px-2">
                            <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 text-[9px] font-bold">
                              {p.privacyClass}
                            </span>
                          </td>
                          <td className="py-2 px-2">{p.required ? 'YES' : 'NO'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Interactive Analytics SDK Tester with Live Scrubber */}
          <div className="bg-slate-900 text-white p-6 rounded-2xl shadow-xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <Code className="w-4 h-4 text-emerald-400" />
                <h3 className="font-bold text-sm">Interactive SDK & Privacy Scrubber Simulator</h3>
              </div>
              <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 text-xs font-mono">
                Live Interceptor
              </span>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-300 mb-1 font-semibold">Event Name:</label>
                <input
                  type="text"
                  value={testEventName}
                  onChange={(e) => setTestEventName(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2 text-white font-mono text-xs"
                />
              </div>

              <div>
                <label className="block text-slate-300 mb-1 font-semibold">
                  Test Payload JSON (Include prohibited attributes like exactSalary or userEmail to test scrubber):
                </label>
                <textarea
                  value={testPayloadJson}
                  onChange={(e) => setTestPayloadJson(e.target.value)}
                  rows={4}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2 text-white font-mono text-xs leading-relaxed"
                />
              </div>

              <button
                onClick={handleRunTestTrack}
                className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs shadow transition-all flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <Play className="w-3.5 h-3.5" /> Execute `analytics.track()`
              </button>

              {testResult && (
                <div className="p-4 bg-slate-800 border border-slate-700 rounded-xl space-y-2 font-mono text-[11px]">
                  {testResult.error ? (
                    <div className="text-red-400 font-bold">{testResult.error}</div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between text-emerald-400 font-bold border-b border-slate-700 pb-1.5">
                        <span>Status: Sanitized & Envelope Wrapped</span>
                        <span>Scrubbed Fields: {testResult.scrubbedFields.length}</span>
                      </div>

                      {testResult.scrubbedFields.length > 0 && (
                        <div className="p-2 bg-red-950/60 border border-red-800/80 rounded text-red-300 space-y-1">
                          <div className="font-bold flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3 text-red-400" /> Scrubber Intercepted & Blocked:
                          </div>
                          <ul className="list-disc list-inside text-[10px]">
                            {testResult.scrubbedFields.map((f: string, idx: number) => (
                              <li key={idx}>{f}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="text-slate-300 pt-1">
                        <div className="text-[10px] text-slate-400 font-bold uppercase">Outbound Sanitized Envelope (GA4 Safe):</div>
                        <pre className="p-2 bg-slate-950 rounded text-slate-200 mt-1 max-h-40 overflow-y-auto">
                          {JSON.stringify(testResult.sanitizedEnvelope, null, 2)}
                        </pre>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
